import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  checkDesktopUpdate,
  checkViewerUpdate,
  desktopStatus,
  installDesktopUpdate,
  installViewerUpdate,
  isDesktopRuntime,
  type DesktopUpdateStatus,
  type ViewerUpdateStatus,
} from "./desktop";

export const desktopStatusQueryKey = ["desktop-status"] as const;
export const desktopServiceUpdateQueryKey = ["desktop-service-update"] as const;
export const desktopViewerUpdateQueryKey = ["desktop-viewer-update"] as const;
export const desktopUpdatePollIntervalMs = 60 * 60 * 1000;

export interface DesktopUpdateOffer {
  readonly available: boolean;
  readonly viewerAvailable: boolean;
  readonly serviceAvailable: boolean;
  readonly currentVersion: string;
  readonly viewerVersion: string;
  readonly serviceVersion: string;
  readonly releaseNotes: string;
}

export function desktopUpdateOffer(
  viewer: ViewerUpdateStatus | undefined,
  service: DesktopUpdateStatus | undefined,
): DesktopUpdateOffer {
  const viewerAvailable = viewer?.available === true;
  const serviceAvailable = service?.available === true;
  const notes = [viewer?.release_notes, service?.release_notes]
    .filter((value): value is string => typeof value === "string")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  return {
    available: viewerAvailable || serviceAvailable,
    viewerAvailable,
    serviceAvailable,
    currentVersion: viewer?.current_version ?? service?.current_version ?? "",
    viewerVersion: viewerAvailable ? viewer.version : "",
    serviceVersion: serviceAvailable ? service.version : "",
    releaseNotes: notes[0] ?? "",
  };
}

export function desktopUpdateButtonLabel(offer: DesktopUpdateOffer): string {
  if (offer.viewerAvailable && offer.serviceAvailable) {
    return `Update to ${offer.viewerVersion}`;
  }
  if (offer.viewerAvailable) {
    return `Update viewer to ${offer.viewerVersion}`;
  }
  if (offer.serviceAvailable) {
    return `Update sync service to ${offer.serviceVersion}`;
  }
  return "Check for updates";
}

export function desktopUpdateConfirmLabel(
  offer: DesktopUpdateOffer,
  pending: boolean,
): string {
  if (pending) return "Installing…";
  return offer.viewerAvailable ? "Install and restart" : "Install update";
}

export function useDesktopUpdates() {
  const queryClient = useQueryClient();
  const desktop = isDesktopRuntime();
  const status = useQuery({
    queryKey: desktopStatusQueryKey,
    queryFn: desktopStatus,
    enabled: desktop,
    refetchInterval: 30_000,
  });
  const enrolled = desktop && status.data?.enrolled === true;
  const service = useQuery({
    queryKey: desktopServiceUpdateQueryKey,
    queryFn: checkDesktopUpdate,
    enabled: enrolled,
    staleTime: desktopUpdatePollIntervalMs,
    refetchInterval: desktopUpdatePollIntervalMs,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: false,
  });
  const viewer = useQuery({
    queryKey: desktopViewerUpdateQueryKey,
    queryFn: checkViewerUpdate,
    enabled: enrolled,
    staleTime: desktopUpdatePollIntervalMs,
    refetchInterval: desktopUpdatePollIntervalMs,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: false,
  });
  const offer = desktopUpdateOffer(viewer.data, service.data);
  const install = useMutation({
    mutationFn: async () => {
      if (offer.serviceAvailable) {
        await installDesktopUpdate();
      }
      if (offer.viewerAvailable) {
        await installViewerUpdate();
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: desktopStatusQueryKey }),
        queryClient.invalidateQueries({
          queryKey: desktopServiceUpdateQueryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: desktopViewerUpdateQueryKey,
        }),
      ]);
    },
  });

  return {
    desktop,
    enrolled,
    status,
    service,
    viewer,
    offer,
    install,
  };
}
